/**
 * Sincronia dos focos com o loop: que focos aparecem em cada quadro, conforme
 * o modo de exibição. Funções puras (testadas em timeFilter.test.ts).
 *
 * Todas as janelas são semiabertas (de, até]: um foco com `t` igual ao
 * instante do quadro pertence a esse quadro.
 */
import type { DisplayMode, TimeWindow } from './types';

export const DEFAULT_STEP = 10 * 60_000;

// Níveis visuais de um foco — só APARÊNCIA: não mudam janelas nem contagens.
/** Detectado no quadro atual (destaque total). */
export const LEVEL_CURRENT = 0;
/** Rastro (modo acumulado): detectado 1 quadro antes — brilho forte. */
export const LEVEL_TRAIL_1 = 1;
/** Rastro (modo acumulado): detectado 2 quadros antes — brilho médio. */
export const LEVEL_TRAIL_2 = 2;
/** Primeiro nível de esmaecimento dos focos antigos (modo acumulado). */
export const FADE_FIRST = 3;
/** Níveis de esmaecimento dos focos antigos no modo acumulado. */
export const FADE_LEVELS = 6;
/** Sem destaque nem esmaecimento (modo "Todo o período"). */
export const LEVEL_NORMAL = FADE_FIRST + FADE_LEVELS;
export const LEVEL_COUNT = LEVEL_NORMAL + 1;

/** Intervalo do quadro atual: distância até o quadro anterior (ou ao seguinte, no primeiro). */
export function frameStep(frames: readonly number[], index: number): number {
  if (frames.length < 2) return DEFAULT_STEP;
  const i = Math.max(0, Math.min(frames.length - 1, index));
  const d = i > 0 ? frames[i]! - frames[i - 1]! : frames[1]! - frames[0]!;
  return d > 0 ? d : DEFAULT_STEP;
}

/**
 * Período do loop para os focos: a UNIÃO dos intervalos dos quadros,
 * (t₀ − passo₀, tₙ]. Assim a soma das contagens de todos os quadros é igual à
 * do período, e o acumulado no quadro i é a soma dos quadros 0…i. Sem quadros,
 * usa a janela `range` com o início exclusivo.
 */
export function loopPeriod(frames: readonly number[], range: TimeWindow | null): { from: number; to: number } | null {
  if (frames.length > 0) return { from: frames[0]! - frameStep(frames, 0), to: frames[frames.length - 1]! };
  return range ? { from: range.start, to: range.end } : null;
}

export interface VisibleWindow {
  /** Exclusivo. */
  from: number;
  /** Inclusivo. */
  to: number;
  /** Focos com t > highlightFrom (e ≤ to) ficam em destaque; null = sem destaque. */
  highlightFrom: number | null;
  /** Esmaecer os mais antigos (modo acumulado). */
  fade: boolean;
  /** Rastro suave nos 2 quadros depois da detecção (modo acumulado). */
  trail: boolean;
  /** Intervalo do quadro atual (ms). */
  step: number;
  /** Referência do esmaecimento: instante atual e duração total. */
  now: number;
  span: number;
}

/**
 * Janela de focos visíveis (`period` = `loopPeriod(...)`):
 * - `frame` (só do quadro atual): (atual − passo, atual], todos em destaque;
 * - `accum` (acumulado): (início do período, atual], destaque no quadro atual,
 *   rastro suave nos 2 quadros seguintes e antigos esmaecidos — no 1º quadro é
 *   igual ao próprio quadro;
 * - `all` (todo o período): (início, fim], estáticos.
 */
export function visibleWindow(
  mode: DisplayMode,
  currentTime: number | null,
  step: number,
  period: { from: number; to: number } | null,
): VisibleWindow | null {
  if (!period) return null;
  const now = currentTime ?? period.to;
  switch (mode) {
    case 'frame':
      return { from: now - step, to: now, highlightFrom: now - step, fade: false, trail: false, step, now, span: step };
    case 'accum': {
      const from = Math.min(period.from, now - step);
      return { from, to: now, highlightFrom: now - step, fade: true, trail: true, step, now, span: Math.max(now - from, step) };
    }
    case 'all':
      return { from: period.from, to: period.to, highlightFrom: null, fade: false, trail: false, step, now, span: period.to - period.from };
  }
}

/** Estado do loop necessário para a janela (tudo lido de UM snapshot do store). */
export interface LoopSnapshot {
  frames: readonly number[];
  index: number;
  currentTime: number | null;
  range: TimeWindow | null;
}

/**
 * Snapshot do loop no instante da imagem EXIBIDA (`displayedTime`): enquanto o
 * quadro pedido ainda decodifica, a imagem anterior continua na tela — e os
 * focos também, para nunca ficarem fora de sincronia com ela. Sem imagem na
 * tela (null) ou instante fora dos quadros, usa o quadro pedido.
 */
export function displayedSnapshot(s: LoopSnapshot & { displayedTime: number | null }): LoopSnapshot {
  const shown = s.displayedTime ?? s.currentTime;
  if (shown === s.currentTime) return s;
  const i = shown === null ? -1 : s.frames.indexOf(shown);
  return i < 0 ? s : { frames: s.frames, range: s.range, index: i, currentTime: shown };
}

/** Janela visível a partir de um snapshot do loop. */
export function windowFor(mode: DisplayMode, t: LoopSnapshot): VisibleWindow | null {
  return visibleWindow(mode, t.currentTime, frameStep(t.frames, t.index), loopPeriod(t.frames, t.range));
}

/**
 * Nível visual de um foco (a mesma regra roda no shader WebGL):
 * - LEVEL_CURRENT: (atual − passo, atual] — quadro atual;
 * - LEVEL_TRAIL_1 / LEVEL_TRAIL_2 (só no acumulado): (atual − 2 passos,
 *   atual − passo] e (atual − 3 passos, atual − 2 passos] — o brilho diminui
 *   aos poucos em vez de apagar de uma vez (o GOES-19 detecta o mesmo fogo de
 *   forma intermitente por causa de nuvens/limiar; sem rastro, piscaria);
 * - FADE_FIRST…FADE_FIRST + FADE_LEVELS − 1: antigos, cada vez menores e apagados;
 * - LEVEL_NORMAL: sem destaque nem esmaecimento (todo o período).
 */
export function ageLevel(t: number, w: VisibleWindow): number {
  if (w.highlightFrom !== null && t > w.highlightFrom) return LEVEL_CURRENT;
  if (!w.fade) return LEVEL_NORMAL;
  if (w.trail) {
    if (t > w.now - 2 * w.step) return LEVEL_TRAIL_1;
    if (t > w.now - 3 * w.step) return LEVEL_TRAIL_2;
  }
  const frac = w.span > 0 ? (w.now - t) / w.span : 1;
  return FADE_FIRST + Math.max(0, Math.min(FADE_LEVELS - 1, Math.floor(frac * FADE_LEVELS)));
}

/** Primeiro índice com ts[i] > v (ts crescente). */
export function upperBound(ts: ArrayLike<number>, v: number, lo = 0, hi = ts.length): number {
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (ts[mid]! <= v) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/** Faixa de índices [lo, hi) com from < t ≤ to. */
export function indexRange(ts: ArrayLike<number>, from: number, to: number): [number, number] {
  const lo = upperBound(ts, from);
  const hi = upperBound(ts, to, lo);
  return [lo, hi];
}
